import React from "react";
import PropTypes from "prop-types";
import {
  createMuiTheme,
  ThemeProvider,
  withStyles
} from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import Hidden from "@material-ui/core/Hidden";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import DnsRoundedIcon from "@material-ui/icons/DnsRounded";
// import PermMediaOutlinedIcon from "@material-ui/icons/PhotoSizeSelectActual";
// import SettingsEthernetIcon from "@material-ui/icons/SettingsEthernet";
import SettingsInputComponentIcon from "@material-ui/icons/SettingsInputComponent";

import _ from "lodash";

import Navigator from "./Navigator";
import Header from "./Header";
import DataContent from "../../data/Content";
import ModelsContent from "../../models/Content";

const sections = [
  {
    id: "Data",
    icon: <DnsRoundedIcon />,
    subsections: ["Browser", "Interpolator"]
  },
  // {
  //   id: "Processing",
  //   icon: <PermMediaOutlinedIcon />,
  //   subsections: ["Not Available"]
  // },
  // {
  //   id: "Functions",
  //   icon: <SettingsEthernetIcon />,
  //   subsections: ["Not Available"]
  // },
  {
    id: "Models",
    icon: <SettingsInputComponentIcon />,
    subsections: ["Browser", "TensorBoard"]
  }
];

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {"Copyright © "}
      <Link color="inherit" href="https://material-ui.com/">
        Material-UI
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

let theme = createMuiTheme({
  palette: {
    primary: {
      light: "#63ccff",
      main: "#009be5",
      dark: "#006db3"
    }
  },
  typography: {
    h5: {
      fontWeight: 500,
      fontSize: 26,
      letterSpacing: 0.5
    }
  },
  shape: {
    borderRadius: 8
  },
  props: {
    MuiTab: {
      disableRipple: true
    }
  },
  mixins: {
    toolbar: {
      minHeight: 48
    }
  }
});

theme = {
  ...theme,
  overrides: {
    MuiDrawer: {
      paper: {
        backgroundColor: "#18202c"
      }
    },
    MuiButton: {
      label: {
        textTransform: "none"
      },
      contained: {
        boxShadow: "none",
        "&:active": {
          boxShadow: "none"
        }
      }
    },
    MuiTabs: {
      root: {
        marginLeft: theme.spacing(1)
      },
      indicator: {
        height: 3,
        borderTopLeftRadius: 3,
        borderTopRightRadius: 3,
        backgroundColor: theme.palette.common.white
      }
    },
    MuiTab: {
      root: {
        textTransform: "none",
        margin: "0 16px",
        minWidth: 0,
        padding: 0,
        [theme.breakpoints.up("md")]: {
          padding: 0,
          minWidth: 0
        }
      }
    },
    MuiIconButton: {
      root: {
        padding: theme.spacing(1)
      }
    },
    MuiTooltip: {
      tooltip: {
        borderRadius: 4
      }
    },
    MuiDivider: {
      root: {
        backgroundColor: "#404854"
      }
    },
    MuiListItemText: {
      primary: {
        fontWeight: theme.typography.fontWeightMedium
      }
    },
    MuiListItemIcon: {
      root: {
        color: "inherit",
        marginRight: 0,
        "& svg": {
          fontSize: 20
        }
      }
    },
    MuiAvatar: {
      root: {
        width: 32,
        height: 32
      }
    }
  }
};

const drawerWidth = 256;

const styles = {
  root: {
    display: "flex",
    minHeight: "100vh"
  },
  "@global": {
    "*::-webkit-scrollbar": {
      width: "0.4em"
    },
    "*::-webkit-scrollbar-track": {
      "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)"
    },
    "*::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0,0,0,.1)",
      outline: "1px solid slategrey"
    }
  },
  drawer: {
    [theme.breakpoints.up("md")]: {
      width: drawerWidth,
      flexShrink: 0
    }
  },
  app: {
    flex: 1,
    display: "flex",
    flexDirection: "column"
  },
  main: {
    flex: 1,
    padding: theme.spacing(6, 4),
    background: "#eaeff1",
    maxWidth: "100vw"
  },
  footer: {
    padding: theme.spacing(2),
    background: "#eaeff1"
  }
};

function Paperbase(props) {
  const { classes, client } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState(
    localStorage.getItem("activeSection") || "Data"
  );

  const getDefaultSubsection = () => {
    if (localStorage.getItem("activeSubsection")) {
      return parseInt(localStorage.getItem("activeSubsection"), 10);
    }
    return 0;
  };
  const [activeSubsection, setActiveSubsection] = React.useState(
    getDefaultSubsection()
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSectionSelection = section => {
    setActiveSection(section);
    localStorage.setItem("activeSection", section);
    handleSubsectionSelection(0);
  };

  const handleSubsectionSelection = index => {
    setActiveSubsection(index);
    localStorage.setItem("activeSubsection", index);
  };

  const sectionData = _.find(sections, section => section.id === activeSection);

  let sectionContent = <React.Fragment />;
  switch (activeSection) {
    case "Data":
      sectionContent = (
        <DataContent client={client} activeSubsection={activeSubsection} />
      );
      break;
    case "Models":
      sectionContent = (
        <ModelsContent client={client} activeSubsection={activeSubsection} />
      );
      break;
    default:
      sectionContent = (
        <DataContent client={client} activeSubsection={activeSubsection} />
      );
  }

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        <CssBaseline />
        <nav className={classes.drawer}>
          <Hidden mdUp implementation="js">
            <Navigator
              sections={sections}
              PaperProps={{ style: { width: drawerWidth } }}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              activeSection={activeSection}
              selectSection={handleSectionSelection}
            />
          </Hidden>
          <Hidden smDown implementation="css">
            <Navigator
              sections={sections}
              PaperProps={{ style: { width: drawerWidth } }}
              activeSection={activeSection}
              selectSection={handleSectionSelection}
            />
          </Hidden>
        </nav>
        <div className={classes.app}>
          <Header
            onDrawerToggle={handleDrawerToggle}
            activeSubsection={activeSubsection}
            handleSubsectionSelection={handleSubsectionSelection}
            sectionData={sectionData}
          />
          <main className={classes.main}>{sectionContent}</main>
          <footer className={classes.footer}>
            <Copyright />
          </footer>
        </div>
      </div>
    </ThemeProvider>
  );
}

Paperbase.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(Paperbase);



// WEBPACK FOOTER //
// ./src/sections/common/components/Paperbase.js